<template>
	<div class='step'>

		<!-- Invoice Details block -->
		<InvoiceSummary v-if='merchant.ShowPaymentSummary' />

		<!-- Address detail collection -->
		<AddressDetails />

		<ErrorMessage :message="error_message" v-if='error_message.length > 0' />
		<WarningMessage :message="warning_message" v-if='warning_message.length > 0' />

		<div class='button-box'>
			<div class='row'>
				<div class='col-xs-12 col-lg-4 order-2 order-lg-1'>
					<a class='start-over' @click="clear()">{{getText('StartOver')}}</a>
				</div>
				<div class='col-xs-12 col-lg-4 order-3 order-lg-2'>
					<div class='contact-text'>
						<b>{{getText('NeedHelp')}}</b> <a class='support-link' :href='"mailto:"+merchant.SupportEmail'>{{merchant.SupportEmail}}</a>
					</div>
				</div>
				<div class='col-xs-12 col-lg-4 order-1 order-lg-3'>
					<input class='submit-button' readonly :value="getText('NextStep')" @click="validate()" >
				</div>
			</div>
		</div>

	</div>
</template>


<script>

import { mapGetters } from "vuex";
import { parsePhoneNumberFromString } from 'libphonenumber-js';

// Language / Localization
import LanguageService from "@/common/language.service";

import { NATIONAL_IDENTIFIER_COUNTRIES, BIRTH_DATE_COUNTRIES, COUNTRY_REGIONS } from "@/config/country.config";

// Import Major components
import InvoiceSummary from "@/components/steps/invoice_step/InvoiceSummary";
import AddressDetails from "@/components/steps/billing_step/AddressDetails";
import Badge from "@/components/general/Badge";

/* Smaller elements */
import WarningMessage from "@/components/messages/WarningMessage";
import ErrorMessage from "@/components/messages/ErrorMessage";

export default {
	name: "BillingStep",
	components: {
		Badge,
		InvoiceSummary,
		AddressDetails,
		WarningMessage,
		ErrorMessage,
	},
	data() {
		// Our primary data objects (used to store user data and progress)
		return {
			error_message: "",
			warning_message: ""
		}
	},
	computed: {
		...mapGetters(["billing", "merchant"]),
		national_identifier_countries(){
			return NATIONAL_IDENTIFIER_COUNTRIES;
		},
		birth_date_countries(){
			return BIRTH_DATE_COUNTRIES;
		}
	},
	methods: {
		getText(key){
			return LanguageService.getText(key);
		},
		getError(key){
			return LanguageService.getError(key);
		},
		clear: function(){
			this.$emit('start_over', true);
		},
		validate: function(){
			this.error_message = '';
			this.warning_message = '';

			if(this.billing.Name.trim().length <= 1) {
				this.error_message = this.getError('InvalidBillingName');
				return;
			}
			if(this.billing.Email.trim().length <= 1) {
				this.error_message = this.getError('InvalidEmailAddress');
				return;
			}
			// Validate email format
			if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.billing.Email)) {
				this.error_message = this.getError('InvalidEmailAddress');
				return;
			}
			if(this.billing.Address.trim().length <= 1) {
				this.error_message = this.getError('InvalidBillingAddress');
				return;
			}
			if(this.billing.City.trim().length <= 1) {
				this.error_message = this.getError('InvalidBillingCity');
				return;
			}

			// Only force region IF we have a list available for them
			if(COUNTRY_REGIONS[this.billing.Country.trim()] !== undefined) {
				if(this.billing.Region.trim().length <= 1) {
					this.error_message = this.getError('InvalidBillingRegion');
					return;
				}
			}
			
			if(this.billing.Country.trim().length != 2) {
				this.error_message = this.getError('InvalidBillingCountry');
				return;
			}
			if(this.billing.PostalCode.trim().length <= 1) {
				this.error_message = this.getError('InvalidBillingPostalCode');
				return;
			}
			if(this.billing.Phone.trim().length <= 1) {
				this.error_message = this.getError('InvalidBillingPhoneNumber');
				return;
			}

			// SPECIAL COUNTRY CASES
			// Selected country requires national identifier?
			if(this.national_identifier_countries.includes(this.billing.Country)){
				// Validate National Identifier
				if(this.billing.NationalIdentifier.length <= 1) {
					this.error_message = this.getError('InvalidBillingNationalIdentifier');
					return;
				}
			} else {
				this.billing.NationalIdentifier = null;
			}

			// Selected country requires birth date
			if(this.birth_date_countries.includes(this.billing.Country)){
				// Validate Birth date
				if(!isValidDate(this.billing.BirthDate)) {
					this.error_message = this.getError('InvalidBillingBirthDate');
					return;
				}
			} else {
				this.billing.BirthDate = null;
			}

			// Wipe payment method selected (country / payment could have changed)
			this.collected_info = {};

			// Convert phone to E.164 if possible
			const formatted_number = parsePhoneNumberFromString(this.billing.Phone, this.billing.Country);
			if(formatted_number){
				this.billing.Phone = formatted_number.number;
			}

			//send a success - tell home to continue
			this.$emit('success', true);
		}
	}
}
</script>
